import React, { useState, useEffect, useMemo } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import type { CreateTarget } from 'actions';
import { Modal, Form, Button, Alert, Table, Spinner, Badge, Card, ListGroup } from 'react-bootstrap';
import { Event } from '../../../../redux/slices/eventsSlice'; // Adjust the path accordingly
import api from '../../../../services/api';
import Select from 'react-select';
import { getAuthorizedZones } from '../../../../redux/slices/lists/zonesSlice';
import { LoadingInput, LoadingSelect } from 'components/forms/LoadingFields';
import { FolderSelect } from 'components/forms/FolderSelect';
import { getQueuesPaginated } from '../../../../redux/slices/lists/queuesSlice';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import FormatDateTime from 'components/common/FormatDateTime';

interface Worker {
  worker_id: number;
  worker_name: string;
}

interface SshConnection {
  connection_id: number;
  connection_name: string;
}

interface Flow {
  flow_id: number;
  flow_name: string;
}

interface RelatedObject {
  node_definition_id: number;
  definition_name: string;
  application: string;
  type: 'Process' | 'Chain';
  triggers_on: 'raise' | 'clear' | 'both';
}

interface EventFileEventProps {
  show: boolean;
  onClose: () => void;
  event?: Event | null; // `event` is optional to support creating a new event
  onSave: (updatedEvent: Event) => Promise<any>; // Expect `onSave` to return a promise with the response or error
  // Workspace create pre-target: seeds the zone + folder on create.
  createIn?: CreateTarget;
}

/** Must match FILE_EVENT_SSH_STATUS_MAX_ENTRIES in nap-backend/websockets/websockets_server.js */
const FILE_EVENT_SSH_STATUS_MAX_STORED = 100;

function sshStatusBadgeVariant(status: string | undefined): string {
  const s = (status || '').toLowerCase();
  if (s.includes('fail') || s.includes('error') || s.includes('_failed') || s.includes('probe_failed')) {
    return 'danger';
  }
  if (
    s.includes('connected') ||
    s.includes('scan_result') ||
    s.includes('move_success') ||
    s.includes('session_established') ||
    s.includes('connection_recovered')
  ) {
    return 'success';
  }
  if (s.includes('connection_lost')) {
    return 'danger';
  }
  if (s.includes('started') || s.includes('monitor_started')) {
    return 'info';
  }
  if (s.includes('stopped')) {
    return 'warning';
  }
  if (s.includes('empty') || s.includes('scan_empty')) {
    return 'warning';
  }
  return 'secondary';
}

const EventFileEvent: React.FC<EventFileEventProps> = ({ show, onClose, event, onSave, createIn }) => {
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState<Event | null>(null);
  const [apiResponse, setApiResponse] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [sshConnections, setSshConnections] = useState<SshConnection[]>([]);
  const [flows, setFlows] = useState<Flow[]>([]);
  const [pollingEnabled, setPollingEnabled] = useState<boolean>(true);
  const reduxError = useAppSelector((state) => state.events.error);

  const { zones, loading: zonesLoadingState } = useAppSelector((state) => state.listOfZones);
  const zonesLoading = !!zonesLoadingState?.getAuthorizedZones;

  const [nodeDefinitions, setNodeDefinitions] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [selectedNodeDetails, setSelectedNodeDetails] = useState<any>(null);
  const [relatedObjects, setRelatedObjects] = useState<RelatedObject[]>([]);
  const [relatedObjectsLoading, setRelatedObjectsLoading] = useState<boolean>(false);
  const [relatedObjectsError, setRelatedObjectsError] = useState<string | null>(null);

  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const isDirtyRaw = useUnsavedChanges(show && !!formData, formData);
  const isDirty = !!event && isDirtyRaw;

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  const { queues, loading: queuesLoadingState } = useAppSelector((state) => state.queues);
  const queuesLoading = !!queuesLoadingState?.getQueuesPaginated;

  const normalizeEventType = (rawType: string | undefined, cfg: any) => {
    if (rawType === 'fileEventSSH') {
      return rawType;
    }
    if (rawType === 'fileEvent') {
      return cfg?.connection_id || cfg?.ssh_connection_string ? 'fileEventSSH' : 'fileEvent';
    }
    return rawType || 'fileEvent';
  };

  // Reset formData to null on close so the next open never sees stale baseline
  useEffect(() => {
    if (!show) {
      setFormData(null);
    }
  }, [show]);

  useEffect(() => {
    if (!show) return;

    if (event) {
      setFormData({
        ...event,
        type: normalizeEventType(event.type, event.config),
      });
      setPollingEnabled(!!event.config?.poll_interval);

      if (event.action?.node_definition_id) {
        const fetchNodeDetails = async () => {
          try {
            const response = await api.get(`/node_definitions/${event.action.node_definition_id}`);
            setSelectedNodeDetails(response.data);
          } catch (error) {
            console.error('Failed to fetch node definition details:', error);
          }
        };
        fetchNodeDetails();
      }
    } else {
      setFormData({
        event_id: 0,
        zone_name: createIn?.zoneName ?? '',
        folder_id: createIn?.folderId ?? null,
        name: '',
        description: '',
        type: 'fileEvent',
        action: {
          type: 'submit_node_definition',
          parameters: [],
          node_definition_id: null,
          queue_id: null,
        },
        config: {
          worker: '',
          directory: '',
          namingpattern: '',
          move: false,
          movedirectory: '',
          poll_interval: 60,
          min_file_age: 30,
          enableFileEvent: true,
          connection_id: null,
          ssh_connection_status_updates: [],
        },
      });
      setSelectedNodeDetails(null);
    }
    setApiResponse(null);
  }, [event, show, createIn]);

  useEffect(() => {
    if (show) {
      const fetchInitialNodeDefinitions = async () => {
        try {
          setSearchLoading(true);
          const response = await api.get('/node_definitions', {
            params: {
              search: '',
              sortColumn: 'name',
              sortOrder: 'asc',
              limit: 20,
            },
          });
          setNodeDefinitions(response.data.nodes);
        } catch (error) {
          console.error('Failed to fetch initial node definitions:', error);
        } finally {
          setSearchLoading(false);
        }
      };

      fetchInitialNodeDefinitions();
    }
  }, [show]);

  useEffect(() => {
    if (searchTerm.length < 2) return;
    const delayDebounceFn = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const response = await api.get(`/node_definitions`, {
          params: {
            search: searchTerm,
            sortColumn: 'name',
            sortOrder: 'asc',
            limit: 20,
          },
        });
        setNodeDefinitions(response.data.nodes); // <-- use `.nodes`
      } catch (error) {
        console.error('Failed to fetch node definitions:', error);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  // Fetch worker data from the API
  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        const response = await api.get('/workers');
        const workerOptions = response.data.workers.map((worker: any) => ({
          worker_id: worker.worker_id,
          worker_name: worker.worker_name,
        }));
        setWorkers(workerOptions);
      } catch (error) {
        console.error('Failed to fetch workers:', error);
      }
    };
    fetchWorkers();
  }, []);

  useEffect(() => {
    const fetchSshConnections = async () => {
      try {
        const response = await api.get('/connections', {
          params: { connection_type: 'ssh', limit: 2000 },
        });
        setSshConnections(response?.data?.connections || []);
      } catch (error) {
        console.error('Failed to fetch SSH connections:', error);
      }
    };
    fetchSshConnections();
  }, []);

  // Fetch flow data from the API
  useEffect(() => {
    const fetchFlows = async () => {
      try {
        const response = await api.get('/flows');
        const flowOptions = response.data.map((flow: any) => ({
          flow_id: flow.flow_id,
          flow_name: flow.flow_name,
        }));
        setFlows(flowOptions);
      } catch (error) {
        console.error('Failed to fetch flows:', error);
      }
    };
    fetchFlows();
  }, []);

  useEffect(() => {
    if (show) {
      dispatch(getQueuesPaginated({}) as any);
    }
  }, [show, dispatch]);

  useEffect(() => {
    if (show) {
      const action = event ? 'update' : 'create';
      dispatch(getAuthorizedZones({ resourceType: 'events', action }) as any);
    }
  }, [show, dispatch]);

  useEffect(() => {
    const fetchRelatedObjects = async () => {
      if (!show || !event?.event_id) {
        setRelatedObjects([]);
        setRelatedObjectsError(null);
        setRelatedObjectsLoading(false);
        return;
      }

      try {
        setRelatedObjectsLoading(true);
        setRelatedObjectsError(null);
        const response = await api.get(`/events/${event.event_id}/related_objects`);
        setRelatedObjects(response?.data?.related_objects || []);
      } catch (error: any) {
        setRelatedObjects([]);
        setRelatedObjectsError(error?.response?.data?.error || 'Failed to load related objects.');
      } finally {
        setRelatedObjectsLoading(false);
      }
    };

    fetchRelatedObjects();
  }, [show, event?.event_id]);

  const handleChange = (field: string, value: any, parentField?: string) => {
    console.log('handleChange - ' + field + ' value: ' + value + ' typeof: ' + typeof value);
    setFormData((prev) => {
      if (!prev) return null;
      if (parentField) {
        return {
          ...prev,
          [parentField]: {
            ...(prev as any)[parentField],
            [field]: value,
          },
        };
      }
      return {
        ...prev,
        [field]: value,
      };
    });
    console.log('formData: ' + JSON.stringify(formData));
  };

  const handleSave = async () => {
    if (formData) {
      console.log('Saving formData:', formData); // Debug data
      setIsSubmitting(true);
      try {
        const baseConfig: any = {
          ...formData.config,
          poll_interval: pollingEnabled ? (formData.config.poll_interval && formData.config.poll_interval > 0 ? formData.config.poll_interval : 60) : 0,
        };
        // SSH runtime details are always resolved from connection_id server-side.
        delete baseConfig.ssh_connection_string;
        delete baseConfig.ssh_shell;

        const payload: any = {
          ...formData,
          config: baseConfig,
        };

        const result = await onSave(payload);
        if (result && result.meta && result.meta.requestStatus === 'rejected') {
          const data = result.payload || result.error;

          const errorMessage = data?.error || data?.message || 'Failed to save event.';

          setApiResponse({
            type: 'error',
            message: errorMessage,
          });

          return;
        }

        setApiResponse({
          type: 'success',
          message: 'Event saved successfully!',
        });
      } catch (error: any) {
        console.error('Save failed:', error);

        const data = error?.response?.data || error;

        const errorMessage = (typeof data === 'string' && data) || data?.error || data?.message || error?.message || 'Failed to save event.';

        setApiResponse({
          type: 'error',
          message: errorMessage,
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const getQueueValue = () => {
    if (!formData?.action?.queue_id) return null;

    const queue = (queues || []).find((q: any) => q.queue_id == formData.action.queue_id);

    if (queue) {
      return {
        value: queue.queue_id,
        label: `${queue.queue_name} (${queue.zone_name})`,
      };
    }
    return {
      value: formData.action.queue_id,
      label: queuesLoading ? 'Loading...' : `Unknown queue (ID: ${formData.action.queue_id})`,
    };
  };

  return (
    <Modal show={show} onHide={handleRequestClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{event ? 'Edit Event' : 'New Event'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Display API response */}
        {apiResponse && (
          <Alert variant={apiResponse.type === 'success' ? 'success' : 'danger'} className="mb-3">
            {apiResponse.message}
          </Alert>
        )}
        <Form>
          {/* Name Field */}
          <LoadingInput
            controlId="name"
            label="Name"
            name="name"
            type="text"
            placeholder=""
            value={formData?.name || ''}
            onChange={(e) => handleChange('name', e.target.value)}
            disabled={isSubmitting}
          />

          {/* Zone Field */}
          <LoadingSelect
            controlId="zone_name"
            label="Zone"
            name="zone_name"
            value={formData?.zone_name || ''}
            onChange={(e) =>
              // Reset the (zone-scoped) folder when the zone changes.
              setFormData((prev) => (prev ? { ...prev, zone_name: e.target.value, folder_id: null } : prev))
            }
            options={(zones || []).map((zone: { name: string }) => ({
              value: zone.name,
              label: zone.name,
            }))}
            isLoading={zonesLoading}
            disabled={isSubmitting}
            placeholder="Select a Zone"
          />

          {/* Folder Field */}
          <Form.Group controlId="event_folder" className="mb-3">
            <Form.Label>Folder</Form.Label>
            <FolderSelect
              value={formData?.folder_id ?? null}
              onChange={(folder_id) => setFormData((prev) => (prev ? { ...prev, folder_id } : prev))}
              zoneName={formData?.zone_name ?? ''}
            />
          </Form.Group>

          {/* Description Field */}
          <Form.Group controlId="description">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              value={formData?.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              disabled={isSubmitting}
            />
          </Form.Group>

          {/* Type Field */}
          <Form.Group controlId="type">
            <Form.Label>Type</Form.Label>
            <Form.Control
              as="select"
              value={formData?.type || ''}
              onChange={(e) => {
                const nextType = e.target.value;
                setFormData((prev) => {
                  if (!prev) return prev;
                  const nextConfig: any = { ...(prev.config || {}) };
                  if (nextType === 'fileEvent') {
                    delete nextConfig.connection_id;
                  } else if (nextType === 'fileEventSSH') {
                    delete nextConfig.worker;
                  }
                  return { ...prev, type: nextType, config: nextConfig };
                });
              }}
              disabled={isSubmitting}
            >
              <option value="fileEvent">File Event via Worker</option>
              <option value="fileEventSSH">File Event via SSH</option>
              <option value="processEvent">Process Event</option>
            </Form.Control>
          </Form.Group>

          {event?.event_id && formData?.type === 'processEvent' && (
            <Form.Group controlId="relatedObjects" className="mt-3">
              <Form.Label>Related Objects</Form.Label>
              {relatedObjectsLoading && (
                <div className="d-flex align-items-center gap-2">
                  <Spinner animation="border" size="sm" />
                  <span>Loading related objects...</span>
                </div>
              )}

              {!relatedObjectsLoading && relatedObjectsError && (
                <Alert variant="warning" className="mb-0">
                  {relatedObjectsError}
                </Alert>
              )}

              {!relatedObjectsLoading && !relatedObjectsError && relatedObjects.length === 0 && (
                <div className="text-muted">No related objects configured for submit on raise/clear.</div>
              )}

              {!relatedObjectsLoading && !relatedObjectsError && relatedObjects.length > 0 && (
                <Table striped bordered hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Definition Name</th>
                      <th>Application</th>
                      <th>Type</th>
                      <th>Triggers On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relatedObjects.map((obj) => (
                      <tr key={obj.node_definition_id}>
                        <td>{obj.definition_name}</td>
                        <td>{obj.application}</td>
                        <td>{obj.type}</td>
                        <td>{obj.triggers_on}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Form.Group>
          )}

          {(formData?.type === 'fileEvent' || formData?.type === 'fileEventSSH') && (
            <>
              <Form.Group controlId="enableFileEvent">
                <Form.Check
                  type="checkbox"
                  label="Enable File Event"
                  checked={formData?.config?.enableFileEvent !== false}
                  onChange={(e) => handleChange('enableFileEvent', e.target.checked, 'config')}
                  disabled={isSubmitting}
                />
              </Form.Group>

              {/* Polling Checkbox and Interval Input */}
              <Form.Group controlId="polling">
                <Form.Check
                  type="checkbox"
                  label="Enable Polling"
                  checked={pollingEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setPollingEnabled(enabled);
                    setFormData((prev) => {
                      if (!prev) return prev;
                      if (!enabled) {
                        return {
                          ...prev,
                          config: {
                            ...prev.config,
                            poll_interval: 0,
                          },
                        };
                      }
                      return {
                        ...prev,
                        config: {
                          ...prev.config,
                          poll_interval: prev.config?.poll_interval && prev.config.poll_interval > 0 ? prev.config.poll_interval : 60,
                        },
                      };
                    });
                  }}
                  disabled={isSubmitting}
                />
              </Form.Group>

              {pollingEnabled && (
                <Form.Group controlId="pollInterval" className="mt-2">
                  <Form.Label>Polling Interval (seconds)</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={formData?.config?.poll_interval || 60}
                    onChange={(e) => handleChange('poll_interval', e.target.value ? parseInt(e.target.value, 10) : null, 'config')}
                    disabled={isSubmitting}
                  />
                </Form.Group>
              )}
              {formData?.type === 'fileEvent' && (
                <Form.Group controlId="worker">
                  <Form.Label>Worker</Form.Label>
                  <Form.Control
                    as="select"
                    value={formData?.config?.worker || ''}
                    onChange={(e) => handleChange('worker', e.target.value, 'config')}
                    disabled={isSubmitting}
                  >
                    <option value="">Select a worker</option>
                    {workers.map((worker) => (
                      <option key={worker.worker_id} value={worker.worker_id}>
                        {worker.worker_name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              )}

              {formData?.type === 'fileEventSSH' && (
                <Form.Group controlId="connection_id">
                  <Form.Label>Connection</Form.Label>
                  <Form.Control
                    as="select"
                    value={formData?.config?.connection_id || ''}
                    onChange={(e) => handleChange('connection_id', e.target.value ? Number(e.target.value) : null, 'config')}
                    disabled={isSubmitting}
                  >
                    <option value="">Select an SSH connection</option>
                    {sshConnections.map((connection) => (
                      <option key={connection.connection_id} value={connection.connection_id}>
                        {connection.connection_name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              )}

              {/* Directory Field */}
              <Form.Group controlId="directory">
                <Form.Label>Directory</Form.Label>
                <Form.Control
                  type="text"
                  value={formData?.config?.directory || ''}
                  onChange={(e) => handleChange('directory', e.target.value, 'config')}
                  disabled={isSubmitting}
                />
              </Form.Group>

              {/* Naming Pattern Field */}
              <Form.Group controlId="namingpattern">
                <Form.Label>Naming Pattern</Form.Label>
                <Form.Control
                  type="text"
                  value={formData?.config?.namingpattern || ''}
                  onChange={(e) => handleChange('namingpattern', e.target.value, 'config')}
                  disabled={isSubmitting}
                />
              </Form.Group>

              {formData?.type === 'fileEventSSH' && (
                <Form.Group controlId="ssh_connection_status_updates" className="mt-4">
                  <div className="d-flex align-items-baseline justify-content-between flex-wrap gap-2 mb-2">
                    <Form.Label className="mb-0">SSH connection status</Form.Label>
                    {(formData?.config?.ssh_connection_status_updates || []).length > 0 && (
                      <small className="text-muted">
                        {(formData?.config?.ssh_connection_status_updates || []).length} stored
                        {(formData?.config?.ssh_connection_status_updates || []).length >= FILE_EVENT_SSH_STATUS_MAX_STORED ? ' (at retention limit)' : ''}
                      </small>
                    )}
                  </div>
                  <Form.Text className="text-muted d-block mb-2">
                    Live tail from agents over SSH. Only the most recent {FILE_EVENT_SSH_STATUS_MAX_STORED.toLocaleString()} entries are kept server-side; older
                    rows are dropped automatically.
                  </Form.Text>
                  {(formData?.config?.ssh_connection_status_updates || []).length === 0 ? (
                    <Card className="border-0 bg-light">
                      <Card.Body className="py-3 text-muted small">
                        No status updates yet. They appear here after a worker handling this SSH connection starts polling.
                      </Card.Body>
                    </Card>
                  ) : (
                    <Card className="shadow-sm border">
                      <ListGroup variant="flush" className="small" style={{ maxHeight: 280, overflowY: 'auto' }}>
                        {(formData?.config?.ssh_connection_status_updates || [])
                          .slice()
                          .reverse()
                          .map((entry: any, idx: number) => (
                            <ListGroup.Item key={`${entry?.timestamp || ''}-${idx}`} className="py-2 px-3">
                              <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                <Badge
                                  bg={sshStatusBadgeVariant(entry?.status)}
                                  className="text-uppercase fw-normal"
                                  style={{
                                    fontSize: '0.7rem',
                                    letterSpacing: '0.02em',
                                  }}
                                >
                                  {entry?.status || 'unknown'}
                                </Badge>
                                <span className="text-muted ms-auto text-nowrap">{entry?.timestamp ? <FormatDateTime value={entry.timestamp} /> : '-'}</span>
                              </div>
                              {entry?.worker_name ? (
                                <div className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>
                                  Worker: <span className="text-body">{entry.worker_name}</span>
                                </div>
                              ) : null}
                              {entry?.detail ? (
                                <pre
                                  className="mb-0 mt-1 text-body"
                                  style={{
                                    fontSize: '0.78rem',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    background: 'var(--bs-tertiary-bg)',
                                    borderRadius: 4,
                                    padding: '0.35rem 0.5rem',
                                  }}
                                >
                                  {entry.detail}
                                </pre>
                              ) : null}
                            </ListGroup.Item>
                          ))}
                      </ListGroup>
                    </Card>
                  )}
                </Form.Group>
              )}

              <Form.Group>
                <Form.Label>Min File Age (seconds)</Form.Label>
                <Form.Control
                  type="number"
                  value={formData?.config?.min_file_age || 30}
                  onChange={(e) => handleChange('min_file_age', parseInt(e.target.value, 10), 'config')}
                  disabled={isSubmitting}
                />
              </Form.Group>

              {/* Move Checkbox */}
              <Form.Group controlId="move">
                <Form.Check
                  type="checkbox"
                  label="Move"
                  checked={formData?.config?.move || false}
                  onChange={(e) => handleChange('move', e.target.checked, 'config')}
                  disabled={isSubmitting}
                />
              </Form.Group>

              {/* Move Directory Field */}
              {formData?.config?.move && (
                <Form.Group controlId="movedirectory">
                  <Form.Label>Move Directory</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData?.config?.movedirectory || ''}
                    onChange={(e) => handleChange('movedirectory', e.target.value, 'config')}
                    disabled={isSubmitting}
                  />
                </Form.Group>
              )}

              {/* Action Type Field */}
              <Form.Group controlId="actionType">
                <Form.Label>Action Type</Form.Label>
                <Form.Control
                  as="select"
                  value={formData?.action?.type || ''}
                  onChange={(e) => handleChange('type', e.target.value, 'action')}
                  disabled={isSubmitting}
                >
                  <option value="submit_node_definition">Submit Node Definition</option>
                </Form.Control>
              </Form.Group>

              {/* Node Definition Searchable Dropdown */}
              <Form.Group controlId="nodeDefinition">
                <Form.Label>Node Definition</Form.Label>
                <Select
                  placeholder="Search and select a Node Definition..."
                  isClearable
                  isLoading={searchLoading}
                  isDisabled={searchLoading || isSubmitting}
                  onInputChange={(inputValue) => {
                    setSearchTerm(inputValue);

                    if (inputValue.length < 2) {
                      // Reload initial node definitions if search is cleared
                      (async () => {
                        try {
                          setSearchLoading(true);
                          const response = await api.get('/node_definitions', {
                            params: {
                              search: '',
                              sortColumn: 'name',
                              sortOrder: 'asc',
                              limit: 20,
                            },
                          });
                          setNodeDefinitions(response.data.nodes);
                        } catch (error) {
                          console.error('Failed to reload node definitions:', error);
                        } finally {
                          setSearchLoading(false);
                        }
                      })();
                    }
                  }}
                  options={nodeDefinitions.map((node) => ({
                    value: node.node_definition_id,
                    label: node.zone_name ? `${node.name} (${node.zone_name})` : node.name,
                  }))}
                  value={
                    formData?.action?.node_definition_id
                      ? {
                          value: formData.action.node_definition_id,
                          label: selectedNodeDetails ? `${selectedNodeDetails.name} (${selectedNodeDetails.zone_name})` : 'Loading...',
                        }
                      : null
                  }
                  onChange={(selectedOption) => {
                    handleChange('node_definition_id', selectedOption ? selectedOption.value : null, 'action');
                    if (selectedOption) {
                      // Fetch node details when a new option is selected
                      const fetchNodeDetails = async () => {
                        try {
                          const response = await api.get(`/node_definitions/${selectedOption.value}`);
                          setSelectedNodeDetails(response.data);
                        } catch (error) {
                          console.error('Failed to fetch node definition details:', error);
                        }
                      };
                      fetchNodeDetails();
                    } else {
                      setSelectedNodeDetails(null);
                    }
                  }}
                />
              </Form.Group>

              <Form.Group controlId="queue">
                <Form.Label>Queue</Form.Label>
                <Select
                  placeholder="Select a queue..."
                  isClearable
                  isLoading={queuesLoading}
                  isDisabled={queuesLoading || isSubmitting}
                  options={(queues || []).map((queue: any) => ({
                    value: queue.queue_id,
                    label: `${queue.queue_name} (${queue.zone_name})`,
                  }))}
                  value={getQueueValue()}
                  onChange={(selectedOption) => {
                    handleChange('queue_id', selectedOption ? selectedOption.value : null, 'action');
                  }}
                />
              </Form.Group>

              {/* Parameters Section */}
              <Form.Group controlId="parameters">
                <Form.Label>Parameters</Form.Label>
                <div>
                  {formData?.action?.parameters.map((param, index) => (
                    <div key={index} className="d-flex mb-2">
                      <Form.Control
                        type="text"
                        placeholder="Name"
                        value={param.name || ''}
                        onChange={(e) =>
                          handleChange(
                            'parameters',
                            [...formData.action.parameters.slice(0, index), { ...param, name: e.target.value }, ...formData.action.parameters.slice(index + 1)],
                            'action'
                          )
                        }
                        className="me-2"
                        disabled={isSubmitting}
                      />
                      <Form.Control
                        type="text"
                        placeholder="Value"
                        value={param.value || ''}
                        onChange={(e) =>
                          handleChange(
                            'parameters',
                            [
                              ...formData.action.parameters.slice(0, index),
                              { ...param, value: e.target.value },
                              ...formData.action.parameters.slice(index + 1),
                            ],
                            'action'
                          )
                        }
                        disabled={isSubmitting}
                      />
                      <Button
                        variant="danger"
                        onClick={() =>
                          handleChange(
                            'parameters',
                            formData.action.parameters.filter((_, i) => i !== index),
                            'action'
                          )
                        }
                        className="ms-2"
                        disabled={isSubmitting}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
                <Button
                  variant="secondary"
                  className="mt-2"
                  onClick={() => handleChange('parameters', [...(formData?.action?.parameters || []), { name: '', value: '' }], 'action')}
                  disabled={isSubmitting}
                >
                  Add Parameter
                </Button>
              </Form.Group>
            </>
          )}

          {formData?.type === 'processEvent' && <>{/* No other fields required for process events */}</>}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleRequestClose} disabled={isSubmitting}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isSubmitting}>
          {event ? 'Save and Close' : 'Create Event'}
        </Button>
      </Modal.Footer>
      <ConfirmationModal
        show={showCloseConfirm}
        title="Unsaved Changes"
        body="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard Changes"
        confirmVariant="danger"
        onConfirm={() => {
          setShowCloseConfirm(false);
          onClose();
        }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </Modal>
  );
};

export default EventFileEvent;
